import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, Users, Navigation, Share2, Edit, Trash2 } from 'lucide-react';

interface Event {
  id: string;
  title: string;
  description?: string;
  event_date: string;
  end_date?: string;
  latitude: number;
  longitude: number;
  location_name: string;
  location_address?: string;
  event_type: 'public' | 'private';
  max_participants?: number;
  registration_deadline?: string;
  enable_chat: boolean;
  enable_checkin: boolean;
  checkin_radius?: number;
  status: 'active' | 'completed' | 'cancelled';
  user_id: string;
  created_at: string;
  cover_image_url?: string;
  participants_count?: number;
  user_registered?: boolean;
}

interface EventCardProps {
  event: Event;
  onJoin: (eventId: string) => void;
  onLeave: (eventId: string) => void;
  onOpenInMaps: (event: Event) => void;
  onEdit?: (event: Event) => void;
  onDelete?: (eventId: string) => void;
  onShare?: (event: Event) => void;
  currentUserId?: string;
}

export const EventCard = ({ event, onJoin, onLeave, onOpenInMaps, onEdit, onDelete, onShare, currentUserId }: EventCardProps) => {
  const isOwner = currentUserId === event.user_id;

  const handleShare = async () => {
    if (onShare) {
      onShare(event);
    } else {
      // Default share behavior
      const eventUrl = `${window.location.origin}/app/events/${event.id}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: event.title,
            text: `${event.description || 'Evento su AstroPet'} - ${event.location_name}`,
            url: eventUrl,
          });
        } catch (error) {
          console.log('Error sharing:', error);
        }
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(eventUrl);
        import('@/hooks/use-toast').then(({ toast }) => {
          toast({
            title: "Link copiato!",
            description: "Il link all'evento è stato copiato negli appunti",
          });
        });
      }
    }
  };
  return (
    <Card className="glass shadow-card border-white/10 hover:shadow-glow transition-all overflow-hidden">
      {event.cover_image_url && (
        <div className="h-48 w-full overflow-hidden">
          <img
            src={event.cover_image_url}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg line-clamp-2">{event.title}</CardTitle>
          <Badge variant={event.event_type === 'public' ? 'default' : 'secondary'}>
            {event.event_type === 'public' ? '🌍 Pubblico' : '🔒 Privato'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-muted-foreground">
            <Clock className="w-4 h-4 mr-2" />
            {new Date(event.event_date).toLocaleDateString('it-IT', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <div className="flex items-center text-muted-foreground">
            <MapPin className="w-4 h-4 mr-2" />
            <span className="line-clamp-1">{event.location_name}</span>
          </div>
          <div className="flex items-center text-muted-foreground">
            <Users className="w-4 h-4 mr-2" />
            {event.participants_count || 0} partecipanti
            {event.max_participants && ` / ${event.max_participants}`}
          </div>
        </div>

        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {event.description}
          </p>
        )}

        {/* Responsive action buttons container */}
        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
          {!isOwner && (
            event.user_registered ? (
              <Button
                size="default"
                variant="outline"
                onClick={() => onLeave(event.id)}
                className="flex-1 min-w-[120px] max-w-[160px] h-10 text-sm px-3"
              >
                <span className="truncate">Annulla Iscrizione</span>
              </Button>
            ) : (
              <Button
                size="default"
                onClick={() => onJoin(event.id)}
                className="flex-1 min-w-[100px] max-w-[140px] h-10 text-sm px-3"
                disabled={event.max_participants && event.participants_count >= event.max_participants}
              >
                <span className="truncate">
                  {event.max_participants && event.participants_count >= event.max_participants ? 'Completo' : 'Iscriviti'}
                </span>
              </Button>
            )
          )}
          
          <Button
            size="default"
            variant="outline"
            onClick={handleShare}
            aria-label="Condividi evento"
            className="min-w-[44px] w-11 h-10 p-0 flex items-center justify-center"
          >
            <Share2 className="w-4 h-4" />
          </Button>

          <Button
            size="default"
            variant="outline"
            asChild
            className="min-w-[44px] w-11 h-10 p-0 flex items-center justify-center"
          >
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apri in Google Maps"
            >
              <Navigation className="w-4 h-4" />
            </a>
          </Button>

          {isOwner && (
            <>
              <Button
                size="default"
                variant="outline"
                onClick={() => onEdit?.(event)}
                aria-label="Modifica evento"
                className="min-w-[44px] w-11 h-10 p-0 flex items-center justify-center"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                size="default"
                variant="destructive"
                onClick={() => onDelete?.(event.id)}
                aria-label="Elimina evento"
                className="min-w-[44px] w-11 h-10 p-0 flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};